import React, { MouseEventHandler, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Destination, DestinationType, Organization } from "../common/ObjectTypes";
import Pagination from "../common/Pagination";
import GlobalSearch from "../common/GlobalSearch";
import { containerClasses, textClasses, buttonClasses, tableClasses, filterClasses, paginationClasses } from "../common/tailwindClassnames";
import { Clear, CopyToClipboard, SortedDown, SortedUp, SortingArrows } from "../common/ProjectIcons";
import NoResults from "../common/NoResults";
import { page_size } from "../../api/apiUtils";
import SelectInput from "../common/SelectInput";
import { toast } from 'react-toastify';

type DestinationsListProps = {
    canAddDestination: boolean,
    reloadDestinations: boolean,
    destinations: Destination[],
    destinationTypes: DestinationType[],
    loadingDestinationTypes: boolean,
    destinationUrls: string[],
    loadingDestinationUrls: boolean,
    organizations: Organization[],
    loadingOrganizations: boolean,
    onAddDestination: MouseEventHandler,
    onViewDetails: Function,
    onChangeGlobalSearch: Function,
    globalSearchStr: string,
    onChangeEndpoint: Function,
    endpointFilterValue: any,
    onChangeType: Function,
    typeFilterValue: any,
    onChangeOwner: Function,
    ownerFilterValue: any,
    requestSort: Function,
    onPageClicked: Function,
    totalCount: number,
    pageLoaded: boolean,
    take: number,
    offset: number
}

const DestinationsList = ({
    canAddDestination,
    reloadDestinations,
    destinations,
    destinationTypes,
    loadingDestinationTypes,
    destinationUrls,
    loadingDestinationUrls,
    organizations,
    loadingOrganizations,
    onAddDestination,
    onViewDetails,
    onChangeGlobalSearch,
    globalSearchStr,
    onChangeEndpoint,
    endpointFilterValue,
    onChangeType,
    typeFilterValue,
    onChangeOwner,
    ownerFilterValue,
    requestSort,
    onPageClicked,
    totalCount,
    pageLoaded,
    take,
    offset
}: DestinationsListProps) => {
    const { t } = useTranslation();
    const [sortDirection, setSortDirection] = useState(false);
    const [sortByColumn, setSortByColumn] = useState("name");

    return (
        <div className={containerClasses.page}>
            <div className="overflow-x-auto max-w-list-table"><div className="min-w-max">
            <div className="mb-12 flex justify-between">
                <div>
                    <h1 data-testid="destinations_page_header" className={textClasses.heading}>
                        {t("Destinations")}
                    </h1>
                </div>
                {canAddDestination && <button className={buttonClasses.primary}
                    onClick={onAddDestination}
                >
                    {t("Create Destination")}
                </button>}
            </div>
            {/* Horizontal filter bar */}
            <div className="flex flex-row gap-3 mb-3 flex-nowrap overflow-y-visible">
                {/* Search */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black">{t("Search")}</span>
                    <GlobalSearch onChangeGlobalSearch={onChangeGlobalSearch} savedString={globalSearchStr} />
                </div>

                {/* URL */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black">{t("URL")}</span>
                    <SelectInput
                        name="base_url__in"
                        options={destinationUrls.map(url => {
                            let hostname = url;
                            try { hostname = new URL(url).hostname; } catch {}
                            return { label: hostname, value: url };
                        })}
                        value={endpointFilterValue}
                        onChange={onChangeEndpoint}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationUrls}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
                {/* Technology */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black">{t("Technology")}</span>
                    <SelectInput
                        name="type__in"
                        options={destinationTypes.map(d => ({ label: d.name, value: d.id }))}
                        value={typeFilterValue}
                        onChange={onChangeType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationTypes}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
                {/* Workspace */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black">{t("Workspace")}</span>
                    <SelectInput
                        name="owner__in"
                        options={organizations.map(org => ({ label: org.name, value: org.id }))}
                        value={ownerFilterValue}
                        onChange={onChangeOwner}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingOrganizations}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
            </div>
            {/* Filter chips */}
            {(globalSearchStr || endpointFilterValue || typeFilterValue || ownerFilterValue) &&
                <div className="flex flex-row flex-wrap gap-2 mb-3">
                    {globalSearchStr && <span className={filterClasses.tag}>
                        {globalSearchStr}
                        <span className="cursor-pointer text-dark-green" onClick={() => onChangeGlobalSearch('')}><Clear /></span>
                    </span>}
                    {endpointFilterValue && <span className={filterClasses.tag}>
                        {(endpointFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green" onClick={() => onChangeEndpoint(null)}><Clear /></span>
                    </span>}
                    {typeFilterValue && <span className={filterClasses.tag}>
                        {(typeFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green" onClick={() => onChangeType(null)}><Clear /></span>
                    </span>}
                    {ownerFilterValue && <span className={filterClasses.tag}>
                        {(ownerFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green" onClick={() => onChangeOwner(null)}><Clear /></span>
                    </span>}
                </div>
            }
            {/* Table */}
            <div className="flex flex-col w-full">
                <table className={tableClasses.container}>
                    <thead className={tableClasses.header}>
                        <tr>
                            <th className={tableClasses.headerCell}>
                                {t("Destination")}
                                <span className={tableClasses.sortIcon} onClick={() => {
                                    requestSort("name", sortDirection)
                                    setSortDirection(!sortDirection)
                                    setSortByColumn("name")
                                }}>
                                    {sortByColumn === "name" ?
                                        sortDirection ? <SortedUp /> : <SortedDown />
                                        : <SortingArrows />}
                                </span>
                            </th>
                            <th className={tableClasses.headerCell}>
                                {t("URL")}
                                <span className={tableClasses.sortIcon} onClick={() => {
                                    requestSort("endpoint", sortDirection)
                                    setSortDirection(!sortDirection)
                                    setSortByColumn("endpoint")
                                }}>
                                    {sortByColumn === "endpoint" ?
                                        sortDirection ? <SortedUp /> : <SortedDown />
                                        : <SortingArrows />}
                                </span>
                            </th>
                            <th className={tableClasses.headerCell}>
                                {t("Technology")}
                                <span className={tableClasses.sortIcon} onClick={() => {
                                    requestSort("type__name", sortDirection)
                                    setSortDirection(!sortDirection)
                                    setSortByColumn("type__name")
                                }}>
                                    {sortByColumn === "type__name" ?
                                        sortDirection ? <SortedUp /> : <SortedDown />
                                        : <SortingArrows />}
                                </span>
                            </th>
                            <th className={tableClasses.headerCell}>
                                {t("Workspace")}
                                <span className={tableClasses.sortIcon} onClick={() => {
                                    requestSort("owner__name", sortDirection)
                                    setSortDirection(!sortDirection)
                                    setSortByColumn("owner__name")
                                }}>
                                    {sortByColumn === "owner__name" ?
                                        sortDirection ? <SortedUp /> : <SortedDown />
                                        : <SortingArrows />}
                                </span>
                            </th>
                        </tr>
                    </thead>
                    {(!pageLoaded || reloadDestinations) ?
                        <tbody className="text-sm">
                            {[...Array(8)].map((_, i) => (
                                <tr key={i} className={tableClasses.row} data-testid="loading">
                                    <td className={tableClasses.cell}><div className="h-4 w-40 bg-neutral-gray-50 rounded animate-pulse" /></td>
                                    <td className={tableClasses.cell}><div className="h-4 w-32 bg-neutral-gray-50 rounded animate-pulse" /></td>
                                    <td className={tableClasses.cell}><div className="h-4 w-32 bg-neutral-gray-50 rounded animate-pulse" /></td>
                                    <td className={tableClasses.cell}><div className="h-4 w-24 bg-neutral-gray-50 rounded animate-pulse" /></td>
                                </tr>
                            ))}
                        </tbody>
                        :
                        <tbody className="text-sm cursor-pointer">
                            {destinations.map((destination: Destination) => {
                                let dest_url: string | URL;
                                try {
                                    dest_url = new URL(destination.base_url).hostname
                                } catch (err) {
                                    dest_url = ""
                                }
                                return (
                                    <tr key={destination.id}
                                        className={tableClasses.row}
                                        onClick={(e) => e.metaKey || e.ctrlKey ? window.open(`/destinations/${destination.id}/configuration`, '_blank') : onViewDetails(destination.id)}
                                    >
                                        <td className={tableClasses.cell}>
                                            {destination.name}
                                        </td>
                                        <td className={`${tableClasses.cell} text-blue-basic`}>
                                            <div className="flex items-center gap-2">
                                                <span>{dest_url}</span>
                                                {destination.base_url && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigator.clipboard.writeText(destination.base_url);
                                                            toast.info(`${t("URL")} ${t("copied to clipboard")}`, {
                                                                position: toast.POSITION.TOP_RIGHT,
                                                                autoClose: 2000
                                                            });
                                                        }}
                                                        className="flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity"
                                                        aria-label="Copy URL to clipboard"
                                                    >
                                                        <CopyToClipboard />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className={tableClasses.cell}>
                                            {destination.type.name}
                                        </td>
                                        <td className={tableClasses.cell}>
                                            {destination.owner.name}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>}
                        {!pageLoaded || reloadDestinations ? null : destinations.length === 0 &&
                            <tbody><tr><td colSpan={4} className="py-16">
                                <NoResults
                                    headerMsg={t("No Results Found")}
                                    labelMsg={t("You may need to adjust your search or filters to find what you are looking for")}
                                />
                            </td></tr></tbody>}
                </table>
                {totalCount > page_size && <Pagination
                    showText={true}
                    handlePageClick={onPageClicked}
                    pageCount={Math.ceil(totalCount / take)}
                    resultStart={offset + 1}
                    resultEnd={(offset + take) < totalCount ? (offset + take) : totalCount}
                    totalCount={totalCount}
                    initialPage={Math.floor(offset / take)}
                    take={take}
                    loading={!pageLoaded}
                    mainclassname={paginationClasses.container}
                />}
            </div>
            </div></div>
        </div>
    )
};

export default DestinationsList;
